import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Circle, CheckCircle2, ExternalLink, Plus } from 'lucide-react';
import { api } from './api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import DialogShell from './kit/DialogShell';
import { errorMessage } from './errors';
import { formatDueDate } from './kit/time';

const when = (iso) => formatDueDate(iso);

export function GhlBadge({ testId }) {
  return (
    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700" data-testid={testId}>GHL</span>
  );
}

// Same POST /tasks the Tasks page uses, with this contact filled in. The task
// is a BFY task even when the contact is GHL-managed (nothing is written to GHL);
// the owner defaults to the caller server-side.
function NewTaskDialog({ open, onOpenChange, contactId, onCreated }) {
  const emptyForm = () => ({ title: '', description: '', due_date: '', priority: 'medium' });
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Give the task a title' }); return; }
    setSaving(true);
    try {
      await api.post('/tasks', {
        ...form,
        title: form.title.trim(),
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        contact_id: contactId,
      });
      toast({ title: 'Task created' });
      setForm(emptyForm());
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast({ title: 'Could not create the task', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogShell open={open} onOpenChange={onOpenChange} title="New Task">
      <div className="space-y-3" data-testid="contact-task-dialog">
        <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="contact-task-title" /></div>
        <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1" data-testid="contact-task-due" /></div>
          <div><Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline-navy" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button variant="accent" onClick={save} disabled={saving} data-testid="contact-task-create">Create</Button>
      </div>
    </DialogShell>
  );
}

// Contact profile → Tasks tab (ticket 135 S3): every task on this contact,
// BFY and GHL-mirrored alike. "Add task" creates a BFY task for this contact
// (ticket 188 row 10). An open task can be completed here — a GHL one only
// when the server can write it to GHL first (`ghl_completable`, ticket 231).
// Reopening and editing happen on the Tasks page; GHL ones stay in GoHighLevel.
const canComplete = (t) => !t.completed && (t.source !== 'ghl' || t.ghl_completable);

export default function ContactTasks({ contactId, ghlEditUrl }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [completing, setCompleting] = useState(null);

  const load = useCallback(() => {
    return api.get('/tasks', { params: { contact_id: contactId } })
      .then(({ data }) => (Array.isArray(data) ? data : []))
      .catch(() => []);
  }, [contactId]);

  useEffect(() => {
    let alive = true;
    load().then((list) => { if (alive) setRows(list); });
    return () => { alive = false; };
  }, [load]);

  const complete = async (t) => {
    setCompleting(t.task_id);
    try {
      await api.patch(`/tasks/${t.task_id}`, { completed: true });
      toast({ title: t.source === 'ghl' ? 'Task completed in GoHighLevel' : 'Task completed' });
      setRows(await load());
    } catch (e) {
      toast({ title: 'Could not complete the task', description: errorMessage(e) });
    } finally {
      setCompleting(null);
    }
  };

  const addBtn = (
    <Button variant="accent" onClick={() => setAdding(true)} data-testid="contact-tasks-add">
      <Plus size={14} /> Add task
    </Button>
  );
  const dialog = (
    <NewTaskDialog open={adding} onOpenChange={setAdding} contactId={contactId} onCreated={() => load().then(setRows)} />
  );

  if (rows === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-tasks-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading tasks...
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-tasks-empty">
        <p className="text-sm">No tasks for this contact.</p>
        <div className="mt-4">{addBtn}</div>
        {dialog}
      </div>
    );
  }
  const open = rows.filter(t => !t.completed);
  const done = rows.filter(t => t.completed);
  return (
    <div className="space-y-3">
      <div className="flex justify-end">{addBtn}</div>
      {dialog}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="contact-tasks-list">
        {[...open, ...done].map(t => {
          const ghl = t.source === 'ghl';
          return (
            <div key={t.task_id} className="flex items-start gap-3 px-4 py-3" data-testid={`contact-task-${t.task_id}`}>
              {t.completed
                ? <CheckCircle2 size={16} className="text-brand-accent mt-0.5 flex-shrink-0" />
                : canComplete(t)
                  ? (
                    <button type="button" onClick={() => complete(t)} disabled={completing === t.task_id}
                      aria-label={`Complete task: ${t.title}`} title={ghl ? 'Completes it in GoHighLevel too' : undefined}
                      className="mt-0.5 flex-shrink-0 disabled:opacity-50" data-testid={`contact-task-complete-${t.task_id}`}>
                      <Circle size={16} className="text-slate-300 hover:text-slate-500" />
                    </button>
                  )
                  : <Circle size={16} className="text-slate-300 mt-0.5 flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium truncate ${t.completed ? 'line-through text-slate-400' : 'text-brand-navy'}`}>{t.title}</span>
                  {ghl && <GhlBadge testId={`ghl-badge-${t.task_id}`} />}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {[when(t.due_date) && `Due ${when(t.due_date)}`, t.owner_name].filter(Boolean).join(' · ')}
                </div>
              </div>
              {ghl && ghlEditUrl && (
                <a href={ghlEditUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs inline-flex items-center gap-0.5 text-indigo-700 underline flex-shrink-0" data-testid={`contact-task-open-ghl-${t.task_id}`}>
                  Open in GHL <ExternalLink size={11} />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
